import { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);

  useEffect(() => {
    // Check if already installed
    const checkIfInstalled = () => {
      // PWA is installed if running in standalone mode
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      // @ts-ignore
      const isInstalled = window.navigator.standalone || isStandalone;
      setIsInstalled(isInstalled);

      // Check localStorage for manual dismissal
      const isDismissed = localStorage.getItem('pwa-install-dismissed');
      if (!isInstalled && !isDismissed) {
        // Show modal after 30 seconds
        const timer = setTimeout(() => {
          setShowInstallPrompt(true);
        }, 30000);
        return () => clearTimeout(timer);
      }
    };

    checkIfInstalled();

    // Listen for beforeinstallprompt event
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Listen for appinstalled event
    const installedHandler = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
    };

    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const installPWA = async () => {
    if (!deferredPrompt) {
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setIsInstalled(true);
    }

    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  const dismissInstallPrompt = () => {
    setShowInstallPrompt(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  return {
    isInstalled,
    canInstall: !!deferredPrompt,
    installPWA,
    showInstallPrompt,
    dismissInstallPrompt,
  };
}
